import React from "react";
import { RefreshCw } from "lucide-react";
import { ErrorBoundaryState } from "../ErrorBoundary";
import { safeString } from "../../../utils/funcs";
import { safeStorage } from "../../../utils/safeStorage";
import { sessionStore } from "../../../utils/sessionStore";
import { Button } from "@/components/ui/button";

const reloadPage = () => window?.location?.reload?.();

export const clearStorage = (): void => {
  try {
    [
      "access_token",
      "agoraInfo",
      "consultationInfo",
      "consultationDetails",
      "rateConsultationStage",
      "waitingRoomStartTime",
      "scheduledConsultationReachedTimer",
      "selectedIssues",
      "providerLogo",
      "createdThrough",
      "hotline",
      "whatsapp",
      "supportEmail",
      "retry",
      "retry_count",
    ].forEach((key) => {
      safeStorage.removeLocalItem(key);
      safeStorage.removeSessionItem(key);
    });
    sessionStore.clearAllAgoraInfo();
  } catch (e) {
    console.error("Failed to clear storage:", e);
  }
};

const clearStorageAndRefresh = (): void => {
  clearStorage();
  reloadPage();
};

// const getConsultationMedium = (path: string) => {
//   if (path.includes("/video")) return "video";
//   if (path.includes("/voice")) return "voice";
//   return "chat";
// };

const ContentContainer = ({ children }: { children: React.ReactNode }) => {
  return <div className="flex justify-center">{children}</div>;
};

export const WaitingRoomErrorContent = ({
  onRetry,
}: {
  onRetry?: () => void;
}) => {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      {onRetry ? (
        <>
          <p className="text-sm text-foreground-muted">
            We couldn&apos;t load your consultation details. Please try again.
          </p>
          <Button
            name="try-again"
            aria-label="Try again"
            data-action="try-again"
            onClick={onRetry}
          >
            <RefreshCw size={16} />
            Try Again
          </Button>
        </>
      ) : (
        <ContentContainer>
          <Button
            name="refresh-waiting-room"
            aria-label="Refresh"
            data-action="refresh-waiting-room"
            onClick={clearStorageAndRefresh}
          >
            <RefreshCw size={16} />
            Refresh
          </Button>
        </ContentContainer>
      )}
    </div>
  );
};

export const ConsultationRoomErrorContent = (props: ErrorBoundaryState) => {
  const userType = props?.userType;
  const consultationId = safeString(props?.consultationId);

  const clearStorageAndRejoin = async () => {
    try {
      const prefix = userType === "doctor" ? "/doctor" : "";

      clearStorage();
      window.location.href = `${prefix}/${consultationId}?err_rejoin=1`;
    } catch (error) {
      console.error(
        "Error from ErrorBoundary refresh consultation button",
        error,
      );
    }
  };

  return (
    <ContentContainer>
      <Button
        name="rejoin-consultation"
        aria-label="Rejoin consultation"
        data-action="rejoin-consultation"
        onClick={() => clearStorageAndRejoin()}
      >
        Rejoin consultation
      </Button>
    </ContentContainer>
  );
};

export const DoctorAddRecommendationErrorContent = (
  props: ErrorBoundaryState,
) => {
  const consultationId = safeString(props?.consultationId);

  const clearStorageAndRejoin = async () => {
    try {
      const prefix = props.userType === "doctor" ? "/doctor" : "";
      clearStorage();
      window.location.href = `${prefix}/${consultationId}?err_rejoin=1`;
    } catch (error) {
      console.error(
        "Error from ErrorBoundary refresh doctor add recommendation button",
        error,
      );
    }
  };

  return (
    <ContentContainer>
      <Button
        name="refresh-recommendations"
        aria-label="Refresh"
        data-action="refresh-recommendations"
        onClick={() => clearStorageAndRejoin()}
      >
        Refresh
      </Button>
    </ContentContainer>
  );
};

export const DefaultErrorContent = () => {
  return (
    <ContentContainer>
      <Button
        name="refresh-page"
        aria-label="Refresh page"
        data-action="refresh-page"
        onClick={clearStorageAndRefresh}
      >
        <RefreshCw size={16} />
        Refresh Page
      </Button>
    </ContentContainer>
  );
};

export const ConsultationRedirectErrorContent = () => {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <p className="text-sm text-foreground-muted">
        Something went wrong while joining your consultation.
      </p>
      <Button
        name="reload-page"
        aria-label="Reload"
        data-action="reload-page"
        onClick={clearStorageAndRefresh}
      >
        <RefreshCw size={16} />
        Reload
      </Button>
    </div>
  );
};
